import { Injectable, inject } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { StaticContentType } from '../../../../environments/environment';
import { filter, map, shareReplay, tap } from 'rxjs/operators';
import { StaticContentService } from '../static-content.service';
import { StaticPageService } from '../../../page/static-page/static-page.service';
import { UserService } from './user.service';
import { UserCompPointsService } from './user-comp-points.service';
import { UserBonusesService } from './user-bonuses.service';
import { BonusStage } from './data/user-bonuses.data';
import { Router } from '@angular/router';

export interface ILoyaltyPage {
  HowWorksTitle: string;
  HowWorksContent: string;
  HowWorksDesc: string;
  BonusTerms: string;
  Title: string;
  Prize: string;
}

export interface ILoyaltyStatic {
  id?: string;
  image?: string;
  pointsFrom?: string | number;
  pointsTo?: string | number;
  bonusMore: string;
  bonusLess: string;
  name?: string;
  level?: any;
  total?: number;
  pts?: number;
  chargeable?: number;
  statusIdx?: number;
  current_percent?: number;
  left_percent?: number;
  left?: number;
  nextStatus: any;
  sublevels?: ILoyaltySublevel[];
}

export interface ILoyaltySublevel {
  boTitle?: string;
  boIdentifier?: string;
  pointsFrom?: number;
  pointsTo?: number;
  fsCount?: number;
  game?: string;
  gameUrl?: string;
  locked?: boolean;
  available?: boolean;
  taken?: boolean;
  name?: string;
  fsTotalPrize?: number;
  isNextLevel?: boolean;
  image?: string;
  availableFs?: any;
  contactSupport?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserLoyaltyService {
  private _static = inject(StaticContentService);
  private _page = inject(StaticPageService);
  private _user = inject(UserService);
  private _comp = inject(UserCompPointsService);
  private _bonuses = inject(UserBonusesService);
  private _router = inject(Router);

  public isLoadedLevels: boolean;
  public isLoadedSubLevels: boolean;

  public currentLevel$: Observable<ILoyaltyStatic>;

  private _pts: any;

  /**
   * Retrieves the loyalty page content from the CMS
   */
  public getLoyaltyPage$(): Observable<ILoyaltyPage> {
    return this._page.item({ slug: `loyalty-program` }).pipe(
      filter(list => !!list),
      map(list => list[0]),
    );
  }

  /**
   * Resolve the loyalty statuses from the CMS and bonus data
   */
  public resolveLoyaltyStatusesCms$(): Observable<ILoyaltyStatic[]> {
    if (this._user.auth) {
      this.isLoadedLevels = false;
      return combineLatest([
        this._static.list({ type: StaticContentType.LOYALTY_STATUSES }),
        this._bonuses.freeSpinsList().pipe(shareReplay(1)),
      ]).pipe(
        filter(list => !!list[0]),
        map(list => this._mapSublevels(list)),
        map(list => this._mapGameUrlAndImage(list)),
        tap(() => (this.isLoadedLevels = true)),
      );
    } else {
      this.isLoadedLevels = true;
    }
  }

  /**
   * Resolve the full loyalty data including current level, points, and progress
   */
  public resolveFullLoyaltyData$(): Observable<ILoyaltyStatic> {
    if (this._user.auth) {
      return combineLatest([
        this._comp.getCompPoints(),
        this._static.list({ type: StaticContentType.LOYALTY_STATUSES }),
      ]).pipe(
        filter(list => !!list[1]),
        map(([points, levelsCMS]) => {
          const level = this.resolveCurrentLevel(levelsCMS);
          const total = Number(levelsCMS[levelsCMS?.length - 1]?.pointsTo);
          const pts = points.persistent;
          const chargeable = points.chargeable;
          const statusData = levelsCMS.find(status => status?.pointsFrom <= pts && status?.pointsTo >= pts) || levelsCMS[levelsCMS.length - 1];
          const statusIdx = levelsCMS.findIndex(status => status === statusData) || 0;

          this._pts = pts;

          let nextStatus;

          if (statusIdx >= 0) {
            nextStatus = levelsCMS[statusIdx + 1] || statusData;
          }

          return {
            ...statusData,
            level,
            total,
            pts,
            chargeable,
            statusIdx,
            nextStatus,
            current_percent: Math.floor(pts / (total / 100)),
            left_percent: 100 - Math.floor(pts / (total / 100)),
            left: total - pts,
          };
        }),
        shareReplay(1),
      );
    }
  }

  public onClickEarn(event) {
    event.preventDefault();
    if (this._user.currentCurrency.amount > 0) {
      this._router.navigateByUrl('/games');
    } else {
      this._router.navigateByUrl('/deposit');
    }
  }

  public resolveCurrentLevel(cmsLevels: any[]) {
    return cmsLevels.find(level =>
      this._user.info.statuses.some(status => status.id === level.id));
  }

  /**
   * Resolves the next level status based on the provided list and index
   * @param list
   * @param onChangeIdx
   */
  public resolveNextLevelStatus(list: ILoyaltyStatic[], onChangeIdx: number): ILoyaltyStatic[] {
    const updatedList = [...list];
    const statusData = list.find((status, i) => i === onChangeIdx);
    const statusIdx = list.findIndex(status => status === statusData) || 0;
    if (statusIdx >= 0 && statusIdx !== 7) {
      updatedList[onChangeIdx]?.sublevels.push({
        name: list[statusIdx + 1]?.name,
        image: list[statusIdx + 1]?.image,
        fsTotalPrize: list[statusIdx + 1]?.sublevels.reduce((acc: any, sublevel) => {
          if (sublevel?.fsCount) {
            return acc + sublevel?.fsCount;
          }
          return acc;
        }, 0),
        isNextLevel: true,
      });
    }
    return updatedList;
  }

  /**
   * Transform string for game url and game image according game name
   * @param str
   * @param isImage
   * @private
   */
  private _transformString(str: string, isImage: boolean) {
    if (!isImage) {
      str = str.replace(/\s/g, '-');
      str = str.replace(/'/g, '');
      str = str.replace(/,/g, '');
      str = str.replace(/&/g, '');
      str = str.replace(/:/g, '');
      return `/play/${ str.toLowerCase() }`;
    } else {
      str = str.replace(/[^\w]|_/g, '');
      return `/assets/img/loyalty/games/${ str.toLowerCase() }.png`;
    }
  }

  private _mapSublevels(list: any[]) {
    return list[0].map(({ pointsFrom, pointsTo, sublevels, ...item }, i) => ({
      ...item,
      pointsFrom: Number(pointsFrom),
      pointsTo: Number(pointsTo),
      sublevels: Array.from(Object.values(sublevels), ({
                                                         boIdentifier,
                                                         pointsFrom,
                                                         pointsTo,
                                                         fsCount,
                                                         ...sublevel
                                                       }) => ({
        ...sublevel,
        pointsFrom: Number(pointsFrom),
        pointsTo: Number(pointsTo),
        fsCount: Number(fsCount),
        locked: (list[1].every(fs => fs?.group_key != boIdentifier) || !list[1].length) && !(this._pts >= Number(pointsFrom)),
        available: list[1].some(fs => fs?.group_key === boIdentifier && [BonusStage.ISSUED].includes(fs?.stage)),
        taken: list[1].some(
          fs =>
            (fs?.group_key === boIdentifier &&
              [BonusStage.ACTIVATED, BonusStage.EXPIRED, BonusStage.FINISHED, BonusStage.LOST].includes(fs?.stage)) ||
            this._pts >= Number(pointsFrom) && !list[1].some(fs => fs?.group_key === boIdentifier && [BonusStage.ISSUED].includes(fs?.stage)),
        ),
        availableFs: list[1].find(fs => fs?.group_key === boIdentifier && [BonusStage.ISSUED].includes(fs?.stage)),
      })),
    }));
  }

  private _mapGameUrlAndImage(list: any[]) {
    return list.map(item => {
      return {
        ...item,
        sublevels: item.sublevels.map(sublevel => {
          return {
            ...sublevel,
            gameUrl: `${ this._transformString(sublevel?.game, false) }`,
            image: `${ this._transformString(sublevel?.game, true) }`,
          };
        }),
      };
    });
  }
}
